import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation, useParams } from "react-router-dom";
import useLoader from "../../../hooks/useLoader";
import {
  Button,
  Checkbox,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import DashboardLayout from "../../../layouts/Dashboard";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { mapPlanData } from "../../../functions/viewDataMap";
import {
  deserializePlanIndicatorsToPlanIndices,
  serializePlanIndicesToIndicatorGroups,
} from "../../../functions/planIndicatorsSerialization";
import { buildPlanRollupFromIndices } from "../../../functions/planRollup";
import { useSelector } from "react-redux";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  departmentCRUD,
  diseaseIndexCRUD,
  diseaseIndexServiceCRUD,
  planCRUD,
  planDocumentCRUD,
  planIndexCRUD,
  planServiceCRUD,
  serviceGroupCRUD,
  serviceMapCRUD,
  servicePriceListCRUD,
  serviceSubGroupCRUD,
  serviceCRUD,
} from "../../../http";
import useAlert from "../../../hooks/useAlert";
import { PLAN_DOCUMENT_ROUTE, PLAN_LIST_ROUTE } from "../../../routes";
import Stack from "@mui/material/Stack";
import { Table } from "../../../components/PDFGenerator/Table";
import RestAutoComplete from "../../../components/Autocompletes/RestAutoComplete";
import { confirmDialog } from "../../../components/Dialog/DialogDelete";
import { commentDialog } from "../../../components/Dialog/CommentDialog";
import { getPlanStateLabel, planStateIds } from "../../../models/planStateIds";
import {
  canEditPlanRowsAtState,
  hasPlanRole,
  isPlanAdmin,
} from "../../../models/planRoles";
import ResearchCodeSelectField from "../../../components/Relations/ResearchCodeSelectField";
import { SERVICE_TYPE_PAYED } from "../../../redux/constants/serviceTypeConstants";
import { js2xml } from "xml-js";
import { useNCALayerClient } from "../../../functions/sign/SignHooks";
import { downloadPlanCodesAgreementReportXlsx } from "../../../functions/planCodesReportXlsx";

const PLAN_TEXT_FIELDS = [
  { key: "inspectedEntityName", label: "Название проверяемого субъекта" },
  { key: "individualOrBusinessId", label: "ID физ./юр. лица" },
  { key: "inspectedObjectName", label: "Название проверяемого объекта" },
  { key: "location", label: "Местоположение" },
];

const PLAN_RELATION_KEYS = [
  "department",
  "filial",
  "filialDivision",
  "division",
  "service",
  "group",
  "subGroup",
  "kind",
  "unit",
  "priceList",
  "contract",
  "state",
  "createdBy",
  "indices",
  "purposeOfSelection",
  "section",
  "subSection",
  "anObjectSection",
  "anObjectSubSection",
  "indicators",
  "Indicators",
];

const parseNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const createEmptyPlanIndex = () => ({
  id: null,
  indexId: "",
  indexName: "",
  serviceId: null,
  departmentId: null,
  departmentName: null,
  groupId: null,
  groupName: null,
  subGroupId: null,
  plannedPlan: 1,
  plannedResearch: 0,
  plannedSum: 0,
  donePlan: 0,
  doneResearch: 0,
  doneSum: 0,
  _isDeleted: false,
  _manualSum: false,
});

const createIndicatorGroup = () => ({
  key: `grp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  departmentId: null,
  departmentName: null,
  groupId: null,
  groupName: null,
  indicators: [],
  loading: false,
});

const planIndexSectionKey = (row) => {
  const hasDept =
    row.departmentId != null && String(row.departmentId).trim() !== "";
  const hasGroup = row.groupId != null && String(row.groupId).trim() !== "";
  if (!hasDept && !hasGroup) return "_none";
  const d = hasDept ? String(row.departmentId) : "_d";
  const g = hasGroup ? String(row.groupId) : "_g";
  return `${d}|${g}`;
};

const sectionLabelForPlanIndexRow = (row) => {
  if (planIndexSectionKey(row) === "_none") return "Без лаборатории";
  const dept =
    row.departmentName || row.department?.name || "Лаборатория";
  const grp =
    row.groupName || row.group?.name || row.serviceGroup?.name || null;
  return grp ? `${dept} — ${grp}` : dept;
};

const buildPlanIndexGroups = (planIndices) => {
  const groups = [];
  const indexByKey = new Map();
  for (let fullIdx = 0; fullIdx < planIndices.length; fullIdx++) {
    const row = planIndices[fullIdx];
    if (row._isDeleted) continue;
    const key = planIndexSectionKey(row);
    if (!indexByKey.has(key)) {
      indexByKey.set(key, groups.length);
      groups.push({
        groupKey: key,
        departmentLabel: sectionLabelForPlanIndexRow(row),
        entries: [],
      });
    }
    groups[indexByKey.get(key)].entries.push({ row, fullIdx });
  }
  return groups;
};

const SYNTHETIC_INDEX_OFFSET = 900000000;

const normalizeIndicatorName = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-zа-я0-9]+/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

const pickRows = (response) => {
  if (Array.isArray(response?.result)) return response.result;
  if (Array.isArray(response?.items)) return response.items;
  return [];
};

const rowMatchesPlanGroupIndicator = (row, group, item) => {
  const rDept =
    row.departmentId != null && row.departmentId !== ""
      ? Number(row.departmentId)
      : null;
  const gDept =
    group.departmentId != null && group.departmentId !== ""
      ? Number(group.departmentId)
      : null;
  if (rDept !== gDept) return false;
  const rG =
    row.groupId != null && row.groupId !== "" ? Number(row.groupId) : null;
  const gG =
    group.groupId != null && group.groupId !== "" ? Number(group.groupId) : null;
  if (rG !== gG) return false;
  if (Number(item.id) >= SYNTHETIC_INDEX_OFFSET) {
    return (
      normalizeIndicatorName(row.indexName) === normalizeIndicatorName(item.name)
    );
  }
  return Number(row.indexId) === Number(item.id);
};

const PlanEditForm = () => {
    const { id } = useParams();
  const alert = useAlert(false, 1);
  const [isNew] = useState(id === "new");
  const locationState = useLocation().state || {};
  const requestedReview = Boolean(locationState?.review);
  const [planDocumentId, setPlanDocumentId] = useState(
    locationState?.planDocumentId != null
      ? Number(locationState.planDocumentId)
      : null
  );
  const backRoute =
    planDocumentId != null
      ? PLAN_DOCUMENT_ROUTE(planDocumentId)
      : PLAN_LIST_ROUTE;
  const [fields, setFields] = useState({});
  const [planMeta, setPlanMeta] = useState({
    stateId:
      locationState?.documentStateId != null
        ? Number(locationState.documentStateId)
        : null,
    stateName: null,
  });
  const [planIndices, setPlanIndices] = useState([]);
  const [planServices, setPlanServices] = useState([]);
  // Опции "код исследования" по строке индикатора (ключ: indexId или name+groupId).
  const [serviceCodeOptionsByKey, setServiceCodeOptionsByKey] = useState({});
  const [serviceByIdCache, setServiceByIdCache] = useState({});
  const [diseaseIndices, setDiseaseIndices] = useState([]);
  const loadServicesForIndicator = useCallback(
    async ({ groupId, indicatorName }) => {
      const gid = Number(groupId);
      const subGroupName = String(indicatorName || "").trim();
      if (!Number.isFinite(gid) || gid <= 0 || !subGroupName) return [];
      const list =
        (await serviceCRUD.post(
          "indicators",
          [{ groupId: gid, subGroupName }],
          "services"
        )) || [];
      return (list || []).filter((s) => {
        if (s?.isDeleted || s?.IsDeleted) return false;
        const st = Number(s?.serviceType ?? s?.ServiceType);
        if (Number.isFinite(st) && st === SERVICE_TYPE_PAYED) return false;
        return true;
      });
    },
    [serviceCRUD]
  );

  const [indexPriceMap, setIndexPriceMap] = useState({});
  const [indexNameIdMap, setIndexNameIdMap] = useState({});
  const [indicatorGroups, setIndicatorGroups] = useState([createIndicatorGroup()]);
  const [selectedObjectSectionName, setSelectedObjectSectionName] = useState("");
  const [loadingIndices, setLoadingIndices] = useState(false);
  const authorizedEmployee = useSelector((state) => state.employee.authorizedEmployee);
  const { loading, start, stop } = useLoader(false);
  const { loading: loadingSubmit, start: startSubmit, stop: stopSubmit } = useLoader(false);
  const { loading: workflowLoading, start: startWorkflow, stop: stopWorkflow } = useLoader(false);
  const { start: startServices, stop: stopServices } = useLoader(false);
  const [ncaClient] = useNCALayerClient();
  const ecpPendingRef = useRef(null);

  const submitPlanData = useMemo(() => mapPlanData(fields), [fields]);

  const planIndexTableGroups = useMemo(
    () => buildPlanIndexGroups(planIndices),
    [planIndices]
  );

  const loadPlanIndices = useCallback(
    async (entityFromFetch) => {
      if (isNew) return;
      try {
        setLoadingIndices(true);
        const entity =
          entityFromFetch ?? (await planCRUD.get(id));
        const fromJson = deserializePlanIndicatorsToPlanIndices(
          entity?.indicators ?? entity?.Indicators
        );
        if (fromJson.length > 0) {
          setPlanIndices(fromJson);
          return;
        }
        const data = await planIndexCRUD.search({
          paging: { pageNumber: 1, pageSize: 1000, returnCount: true },
          filter: {
            planId: { operand1: String(id), operator: "equals" },
          },
          sort: {
            id: { operator: "asc", ordinal: 0 },
          },
        });

        const rows = (data?.result || []).map((row) => ({
          id: row.id,
          indexId: row.indexId ?? "",
          indexName: row.index?.name || row.indexName || "",
          departmentId: row.departmentId ?? row.department?.id ?? null,
          departmentName: row.department?.name ?? null,
          groupId: row.groupId ?? row.group?.id ?? row.serviceGroupId ?? null,
          groupName: row.group?.name ?? row.serviceGroup?.name ?? null,
          plannedPlan: row.plannedPlan ?? 1,
          plannedResearch: row.plannedResearch ?? 0,
          plannedSum: row.plannedSum ?? 0,
          donePlan: row.donePlan ?? 0,
          doneResearch: row.doneResearch ?? 0,
          doneSum: row.doneSum ?? 0,
          _isDeleted: false,
        }));
        setPlanIndices(rows);
      } catch (error) {
        alert.show(0, error);
      } finally {
        setLoadingIndices(false);
      }
    },
    [alert, id, isNew]
  );

  const loadPlanServices = useCallback(async () => {
    if (isNew) return;
    try {
      startServices();
      const query1 = {
        paging: { skip: 0, take: null, returnCount: true },
        filter: {
          planId: { operand1: String(id), operator: "equals" },
        },
        sort: { id: { operator: "asc", ordinal: 0 } },
        includes: ["Service", "Department", "Division"],
      };
      const data = await planServiceCRUD.search(query1);
      const rows1 = data?.result || [];

      // Фолбэк: некоторые бэки ожидают PascalCase ключи (PlanId) и явный pageNumber/pageSize.
      if (!rows1.length) {
        const data2 = await planServiceCRUD.search({
          paging: { pageNumber: 1, pageSize: 1000, returnCount: true },
          filter: {
            PlanId: { operand1: String(id), operator: "equals" },
          },
          sort: { id: { operator: "asc", ordinal: 0 } },
          includes: ["Service", "Department", "Division"],
        });
        setPlanServices(data2?.result || []);
      } else {
        setPlanServices(rows1);
      }
    } catch (error) {
      // секция необязательная; но ошибку покажем
      alert.show(0, error);
    } finally {
      stopServices();
    }
  }, [alert, id, isNew, startServices, stopServices]);

  const loadDiseaseIndices = useCallback(async () => {
    try {
      const data = await diseaseIndexCRUD.search({
        paging: { skip: 0, take: null, returnCount: true },
        sort: { id: { operator: "asc", ordinal: 0 } },
      });
      setDiseaseIndices(data?.result || []);
    } catch (error) {
      alert.show(0, error);
    }
  }, [alert]);

    const fetch = useCallback(async () => {
        try {
      start();
            if (!isNew) {
        const data = await planCRUD.get(id);
        const stateCode = String(data?.state?.code ?? data?.state?.Code ?? "").trim().toLowerCase();
        const stateIdFromCode = (() => {
          const map = {
            undefined: planStateIds.undefined,
            created: planStateIds.created,
            filling: planStateIds.filling,
            senttonce: planStateIds.sentToNce,
            transferredtobranch: planStateIds.transferredToBranch,
            inprogressnce: planStateIds.inProgressNce,
            editedbyexecutor: planStateIds.editedByExecutor,
            onslaapproval: planStateIds.onSlaApproval,
            sentforapproval: planStateIds.sentForApproval,
            revision: planStateIds.revision,
            returnedforcorrection: planStateIds.returnedForCorrection,
            agreed: planStateIds.agreed,
            slaapproved: planStateIds.slaApproved,
            onapproval: planStateIds.onApproval,
            transferredtodepartmenthead: planStateIds.transferredToDepartmentHead,
            approved: planStateIds.approved,
            final: planStateIds.final,
            declined: planStateIds.declined,
            rejected: planStateIds.rejected,
          };
          return map[stateCode] ?? null;
        })();

        const stateName = data?.state?.name ?? data?.state?.Name ?? null;
        const stateIdFromName = (() => {
          const n = String(stateName || "").trim().toLowerCase();
          if (!n) return null;
          const mapRu = {
            "новая": planStateIds.created,
            "заполняется": planStateIds.filling,
            "отправлен в нцэ": planStateIds.sentToNce,
            "передан на обработку филиалу нцэ": planStateIds.transferredToBranch,
            "в работе нцэ": planStateIds.inProgressNce,
            "редактируется исполнителем": planStateIds.editedByExecutor,
            "на согласовании сла": planStateIds.onSlaApproval,
            "отправлен на согласование": planStateIds.sentForApproval,
            "на доработке": planStateIds.revision,
            "возвращён на исправление": planStateIds.returnedForCorrection,
            "согласован": planStateIds.agreed,
            "одобрен сла": planStateIds.slaApproved,
            "на утверждении": planStateIds.onApproval,
            "передан руководителю департамента": planStateIds.transferredToDepartmentHead,
            "утверждён": planStateIds.approved,
            "финальный статус": planStateIds.final,
            "отклонён": planStateIds.declined,
            "отказ на любом этапе": planStateIds.rejected,
          };
          return mapRu[n] ?? null;
        })();

        const stateId =
          data?.stateId ??
          data?.state?.id ??
          data?.state?.Id ??
          stateIdFromCode ??
          stateIdFromName ??
          null;

        const docId =
          data?.planDocumentId ??
          data?.PlanDocumentId ??
          data?.planDocument?.id ??
          locationState?.planDocumentId ??
          null;
        let docFilialId = null;
        let docFilialDivisionId = null;
        let docContractId = null;
        let docDistrictCity = "";
        let docPeriod = null;
        if (docId != null) {
          setPlanDocumentId(Number(docId));
          try {
            const doc = await planDocumentCRUD.get(docId);
            const docStateId =
              doc?.stateId ?? doc?.state?.id ?? doc?.state?.Id ?? null;
            const docStateName =
              doc?.state?.name ||
              doc?.state?.Name ||
              getPlanStateLabel(docStateId, null);
            setPlanMeta({
              stateId:
                docStateId != null
                  ? Number(docStateId)
                  : stateId != null
                    ? Number(stateId)
                    : null,
              stateName: docStateName || stateName,
            });
            docFilialId = doc?.filialId ?? doc?.filial?.id ?? null;
            docFilialDivisionId =
              doc?.filialDivisionId ?? doc?.filialDivision?.id ?? null;
            docContractId = doc?.contractId ?? doc?.contract?.id ?? null;
            docDistrictCity = doc?.districtCity ?? "";
            const rawMonth = doc?.month ?? doc?.Month;
            if (rawMonth != null && rawMonth !== "") {
              let iso = rawMonth;
              if (
                typeof rawMonth === "string" &&
                /^\d{4}-\d{2}-\d{2}T/.test(rawMonth) &&
                !/(Z|[+-]\d{2}:?\d{2})$/i.test(rawMonth)
              ) {
                iso = `${rawMonth}Z`;
              }
              const asDate = new Date(iso);
              if (!Number.isNaN(asDate.getTime())) {
                docPeriod = new Date(asDate.getFullYear(), asDate.getMonth(), 1);
              }
            }
            if (!docPeriod) {
              const y = Number(doc?.year);
              if (Number.isFinite(y) && y >= 2000 && y <= 2100) {
                docPeriod = new Date(y, 0, 1);
              }
            }
          } catch {
            setPlanMeta({
              stateId: stateId != null ? Number(stateId) : null,
              stateName,
            });
          }
        } else {
          setPlanMeta({
            stateId: stateId != null ? Number(stateId) : null,
            stateName,
          });
        }
        setFields({
                        departmentId: data.department?.id || null,
                        department: data.department || null,
                        filialId:
                          docFilialId ?? data.filial?.id ?? data.filialId ?? null,
                        filial: data.filial || null,
                        filialDivisionId:
                          docFilialDivisionId ??
                          data.filialDivisionId ??
                          data.filialDivision?.id ??
                          null,
                        filialDivision: data.filialDivision || null,
                        divisionId: data.divisionId || null,
                        division: data.division || null,
                        month: data.month,
          year:
            docPeriod ||
            (data.month ? new Date(data.month) : new Date()),
          amount: data.amount ?? 0,
          sum: data.sum ?? 0,
                        contractId:
                          docContractId ?? data.contractId ?? null,
                        serviceId: data.serviceId || null,
                        service: data.service || null,         
          price: data.price ?? 0,
          priceListId: data.priceListId || null,
          districtCity:
            docDistrictCity || data.districtCity || "",
          inspectedEntityName: data.inspectedEntityName || "",
          individualOrBusinessId: data.individualOrBusinessId || "",
          inspectedObjectName: data.inspectedObjectName || "",
          location: data.location || "",
          month1: data.month1 ?? 0,
          month2: data.month2 ?? 0,
          month3: data.month3 ?? 0,
          month4: data.month4 ?? 0,
          month5: data.month5 ?? 0,
          month6: data.month6 ?? 0,
          month7: data.month7 ?? 0,
          month8: data.month8 ?? 0,
          month9: data.month9 ?? 0,
          month10: data.month10 ?? 0,
          month11: data.month11 ?? 0,
          month12: data.month12 ?? 0,
          purposeId:
            data.purposeId ??
            data.purposeOfSelection?.id ??
            data.purposeOfSelectionId ??
            null,
          sectionId:
            data.sectionId ??
            data.section?.id ??
            data.anObjectSection?.id ??
            null,
          subSectionId:
            data.subSectionId ??
            data.subSection?.id ??
            data.anObjectSubSection?.id ??
            null,
        });
        setSelectedObjectSectionName(
          data.section?.name ?? data.anObjectSection?.name ?? ""
        );
        await loadPlanIndices(data);
        // Создание списка исследований как в заявке из предписания
        await loadServices(data);
      } else {
        setPlanMeta({ stateId: null, stateName: null });
        setFields({
          month: new Date(),
          year: new Date(),
          amount: 0,
          sum: 0,
          districtCity: "",
          inspectedEntityName: "",
          individualOrBusinessId: "",
          inspectedObjectName: "",
          location: "",
          month1: 0,
          month2: 0,
          month3: 0,
          month4: 0,
          month5: 0,
          month6: 0,
          month7: 0,
          month8: 0,
          month9: 0,
          month10: 0,
          month11: 0,
          month12: 0,
          purposeId: null,
          sectionId: null,
          subSectionId: null,
        });
        setSelectedObjectSectionName("");
      }
    } catch (err) {
      alert.show(0, "Ошибка загрузки!");
    } finally {
      stop();
    }
  }, [alert, id, isNew, loadPlanIndices, locationState?.planDocumentId, start, stop]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  useEffect(() => {
    loadDiseaseIndices();
  }, [loadDiseaseIndices]);

  useEffect(() => {
    if (isNew) return;
    loadPlanServices();
  }, [isNew, loadPlanServices]);

  const submit = useCallback(async () => {
    if (isNew) return;

    try {
      startSubmit();
      const rollup = buildPlanRollupFromIndices(planIndices, fields);
      const payload = {
        id: Number(id),
        ...submitPlanData,
        ...rollup,
        sum: rollup.sum || parseNumber(fields.sum),
        month: fields.year ? new Date(fields.year).toISOString() : fields.month,
        createdById: fields.createdById || authorizedEmployee?.id || null,
      };

      PLAN_RELATION_KEYS.forEach((key) => {
        delete payload[key];
      });

      // Статус живёт на PlanDocument — не прокидываем document.stateId в строку Plan.
      delete payload.stateId;
      delete payload.state;

      if (planDocumentId != null && Number.isFinite(Number(planDocumentId))) {
        payload.planDocumentId = Number(planDocumentId);
      }

      payload.indicators = JSON.stringify(
        serializePlanIndicesToIndicatorGroups(planIndices)
      );

      await planCRUD.edit(payload);
      await loadPlanIndices();
      alert.show(1, "Изменения сохранены");
    } catch (error) {
      alert.show(0, error);
    } finally {
      stopSubmit();
    }
  }, [
    alert,
    authorizedEmployee?.id,
    fields.month,
    fields.year,
    id,
    isNew,
    loadPlanIndices,
    planDocumentId,
    planIndices,
    startSubmit,
    stopSubmit,
    submitPlanData,
  ]);

  // Явная кнопка "Сохранить" (без изменения статуса).
  const saveOnly = useCallback(async () => {
    await submit();
  }, [submit]);

  // Сохранение indicators перед воркфлоу должно быть "полным" сохранением,
  // иначе бэк может обнулить поля (договор/филиал/месяц и т.д.) при частичном payload.
  const persistIndicatorsJson = useCallback(async () => {
    await submit();
  }, [submit]);

  const isNceExecutionStage = useMemo(() => {
    const st = Number(planMeta.stateId);
    return st === planStateIds.inProgressNce;
  }, [planMeta.stateId]);

  // Выбор кода исследования — этап НЦЭ.
  const isNceCodeSelectionStage = useMemo(() => {
    const st = Number(planMeta.stateId);
    return st === planStateIds.inProgressNce;
  }, [planMeta.stateId]);

  const isAfterSentForApproval = useMemo(() => {
    const st = Number(planMeta.stateId);
    return [
      planStateIds.onSlaApproval,
      planStateIds.onApproval,
      planStateIds.approved,
      planStateIds.final,
      // legacy
      planStateIds.sentForApproval,
      planStateIds.agreed,
      planStateIds.slaApproved,
      planStateIds.transferredToDepartmentHead,
    ].includes(st);
  }, [planMeta.stateId]);

  // После согласования СЛА и дальше — только просмотр.
  const review = useMemo(
    () =>
      requestedReview ||
      isAfterSentForApproval ||
      !canEditPlanRowsAtState(planMeta.stateId),
    [isAfterSentForApproval, planMeta.stateId, requestedReview]
  );

  const showResearchCodeColumn = isNceCodeSelectionStage || isAfterSentForApproval;

  const canEditNceIndices = useMemo(() => {
    if (!isNceExecutionStage) return false;
    return isPlanAdmin() || hasPlanRole("performer", "planning");
  }, [isNceExecutionStage]);

  const [servicesAutoGenerated, setServicesAutoGenerated] = useState(false);
  const [servicesGenerationTried, setServicesGenerationTried] = useState(false);
  const lastServicesGenSignatureRef = useRef("");

  useEffect(() => {
    // Если попытка автогенерации была слишком ранней (например, до подгрузки индексов/справочников),
    // даём механизму шанс повторить генерацию при изменении входных данных.
    if (!isNceExecutionStage) return;
    const hasAnyPlanServicesLocal = (planServices || []).some((x) => !x?._isDeleted);
    if (hasAnyPlanServicesLocal) return;
    const active = (planIndices || []).filter((x) => !x?._isDeleted);
    if (!active.length) return;
    const signature = active
      .map((r) => `${String(r?.indexId ?? "")}|${String(r?.serviceId ?? "")}|${String(r?.indexName ?? "")}`)
      .join(";;");
    if (signature && signature !== lastServicesGenSignatureRef.current) {
      lastServicesGenSignatureRef.current = signature;
      setServicesGenerationTried(false);
    }
  }, [diseaseIndices?.length, isNceExecutionStage, planIndices, planServices]);

  const showManualSumHint = useMemo(() => {
    const st = Number(planMeta.stateId);
    return st === planStateIds.inProgressNce;
  }, [planMeta.stateId, review]);

  const showPlannedSumColumn = useMemo(() => {
    const st = Number(planMeta.stateId);
    return [
      planStateIds.inProgressNce,
      planStateIds.onSlaApproval,
      planStateIds.onApproval,
      planStateIds.approved,
      planStateIds.final,
      // legacy
      planStateIds.sentToNce,
      planStateIds.revision,
      planStateIds.sentForApproval,
      planStateIds.agreed,
      planStateIds.slaApproved,
      planStateIds.transferredToDepartmentHead,
    ].includes(st);
  }, [planMeta.stateId, review]);

  const showIndicesSection = true;

  const isIndicesReadOnly = useMemo(() => {
    if (isAfterSentForApproval) return true;
    // Created: Planning редактирует показатели; NCE: коды/суммы.
    if (Number(planMeta.stateId) === planStateIds.created) {
      return !(isPlanAdmin() || hasPlanRole("planning"));
    }
    if (review && canEditNceIndices) return false;
    return review || isNceExecutionStage;
  }, [canEditNceIndices, isAfterSentForApproval, isNceExecutionStage, planMeta.stateId, review]);

  useEffect(() => {
    // Для read-only стадий после отправки на согласование догружаем service по serviceId,
    // чтобы в колонке "Код исследования" отображался код/название, а не только id.
    if (!showResearchCodeColumn) return;
    const ids = Array.from(
      new Set(
        (planIndices || [])
          .map((r) => Number(r?.serviceId))
          .filter((sid) => Number.isFinite(sid) && sid > 0)
      )
    ).filter((sid) => !serviceByIdCache?.[sid]);
    if (!ids.length) return;

    let cancelled = false;
    (async () => {
      const pairs = await Promise.all(
        ids.map(async (sid) => {
          try {
            const s = await serviceCRUD.get(sid);
            return [sid, s || null];
          } catch {
            return [sid, null];
          }
        })
      );
      if (cancelled) return;
      setServiceByIdCache((prev) => {
        const next = { ...(prev || {}) };
        pairs.forEach(([sid, s]) => {
          if (s) next[sid] = s;
        });
        return next;
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [planIndices, serviceByIdCache, serviceCRUD, showResearchCodeColumn]);

  // Подгружаем варианты кода исследования (services) для каждой строки плана.
  useEffect(() => {
    if (!isNceCodeSelectionStage) return;
    if (isNew) return;
    const activeRows = (planIndices || []).filter((r) => !r?._isDeleted);
    if (!activeRows.length) return;

    const keyForRow = (row, resolvedIndexId = null) => {
      const idxId = Number(resolvedIndexId ?? row?.indexId);
      if (Number.isFinite(idxId) && idxId > 0) return `idx_${idxId}`;
      const g = Number(row?.groupId);
      const gPart = Number.isFinite(g) && g > 0 ? String(g) : "_";
      const nPart = normalizeIndicatorName(row?.indexName);
      return `name_${gPart}_${nPart || "_"}`;
    };

    const missingRows = activeRows.filter((row) => {
      const idxId = Number(row?.indexId);
      const key = Number.isFinite(idxId) && idxId > 0 ? `idx_${idxId}` : null;
      if (key && serviceCodeOptionsByKey[key]) return false;
      // для строк без indexId ключ будет на основе name+groupId
      const fallbackKey = keyForRow(row, null);
      return !serviceCodeOptionsByKey[fallbackKey];
    });
    if (!missingRows.length) return;

    let cancelled = false;
    (async () => {
      const updates = [];

      for (const row of missingRows) {
        try {
          const resolvedIndexId = await resolveRowIndexId(row);
          const key = keyForRow(row, resolvedIndexId);
          if (serviceCodeOptionsByKey[key]) continue;

          const opts = [];
          const seen = new Set();

          // 1) Пытаемся взять из diseaseIndex.services
          const idxIdNum = Number(resolvedIndexId);
          if (Number.isFinite(idxIdNum) && idxIdNum > 0) {
            try {
              const di = await diseaseIndexCRUD.get(idxIdNum);
              const directSid = Number(di?.serviceId ?? di?.ServiceId ?? di?.service?.id);
              if (Number.isFinite(directSid) && directSid > 0) {
                seen.add(directSid);
              }
              const services = Array.isArray(di?.services) ? di.services : [];
              for (const s of services) {
                const sid = Number(s?.serviceId ?? s?.service?.id ?? s?.id);
                if (!Number.isFinite(sid) || sid <= 0) continue;
                seen.add(sid);
              }
            } catch {
              // ignore
            }
          }

          // 2) Фолбэк: через diseaseIndexServiceCRUD по indexId
          if (seen.size === 0) {
            const idxIdNum = Number(resolvedIndexId);
            if (Number.isFinite(idxIdNum) && idxIdNum > 0) {
              try {
                const disResp = await diseaseIndexServiceCRUD.search({
                  paging: { skip: 0, take: 50, returnCount: false },
                  filter: {
                    indexId: { operand1: String(idxIdNum), operator: "equals" },
                  },
                });
                const disRows = pickRows(disResp);
                disRows.forEach((item) => {
                  const sid = Number(item?.serviceId);
                  if (Number.isFinite(sid) && sid > 0) seen.add(sid);
                });
              } catch {
                // ignore
              }
            }
          }

          // 3) Фолбэк: как в автогенерации услуг — по индикатору (groupId + subGroupName) -> services
          if (seen.size === 0) {
            const gid = Number(row?.groupId);
            const subGroupName = String(row?.indexName || "").trim();
            if (Number.isFinite(gid) && gid > 0 && subGroupName) {
              try {
                const services =
                  (await serviceCRUD.post(
                    "indicators",
                    [{ groupId: gid, subGroupName }],
                    "services"
                  )) || [];
                (services || []).forEach((s) => {
                  const sid = Number(s?.id);
                  if (Number.isFinite(sid) && sid > 0) {
                    // здесь уже есть code/name
                    if (!opts.some((x) => Number(x.serviceId) === sid)) {
                      const code = String(s?.code || "").trim();
                      const name = String(s?.name || "").trim();
                      const dept = String(s?.department?.name || "").trim();
                      const div = String(s?.division?.name || "").trim();
                      const sg = String(s?.subGroup?.name || s?.subGroupName || "").trim();
                      const kind = String(s?.kind?.name || "").trim();
                      const tailParts = [dept, div, sg, kind].filter(Boolean);
                      const tail = tailParts.length ? ` — ${tailParts.join(" / ")}` : "";
                      opts.push({
                        serviceId: sid,
                        code: code || String(sid),
                        label: code
                          ? name
                            ? `${code} - ${name}${tail}`
                            : `${code}${tail}`
                          : `ID ${sid}${tail}`,
                      });
                    }
                    seen.add(sid);
                  }
                });
              } catch {
                // ignore
              }
            }
          }

          // 4) Если собрали только serviceId — догружаем service для кода/названия
          if (opts.length === 0 && seen.size > 0) {
            const ids = Array.from(seen);
            const services = await Promise.all(
              ids.map(async (sid) => {
                try {
                  const s = await serviceCRUD.get(sid);
                  return s ? { sid, s } : null;
                } catch {
                  return null;
                }
              })
            );
            services
              .filter(Boolean)
              .forEach(({ sid, s }) => {
                const code = String(s?.code || "").trim();
                const name = String(s?.name || "").trim();
                const dept = String(s?.department?.name || "").trim();
                const div = String(s?.division?.name || "").trim();
                const sg = String(s?.subGroup?.name || "").trim();
                const kind = String(s?.kind?.name || "").trim();
                const tailParts = [dept, div, sg, kind].filter(Boolean);
                const tail = tailParts.length ? ` — ${tailParts.join(" / ")}` : "";
                opts.push({
                  serviceId: Number(sid),
                  code: code || String(sid),
                  label: code
                    ? name
                      ? `${code} - ${name}${tail}`
                      : `${code}${tail}`
                    : `ID ${sid}${tail}`,
                });
              });
          }

          updates.push({ key, opts: opts.sort((a, b) => a.label.localeCompare(b.label, "ru")) });
        } catch {
          // ignore row
        }
      }

      if (cancelled) return;
      if (!updates.length) return;
      setServiceCodeOptionsByKey((prev) => {
        const next = { ...prev };
        updates.forEach(({ key, opts }) => {
          next[key] = opts;
        });
        return next;
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [
    diseaseIndexCRUD,
    diseaseIndexServiceCRUD,
    isNew,
    isNceCodeSelectionStage,
    planIndices,
    serviceCRUD,
    serviceCodeOptionsByKey,
  ]);

  const persistPlanServices = useCallback(
    async ({ services, deleteIds }) => {
      // удаление
      for (const serviceId of deleteIds || []) {
        if (serviceId == null) continue;
        await planServiceCRUD.delete(serviceId);
      }

      // upsert (простой подход: edit если есть id, иначе create)
      for (const row of services || []) {
        // Держим payload минимальным: на бэке часто валится на лишних/null полях.
        // После create/edit перечитываем список с includes — UI заполнится.
        const payload = {
          id: row?.id ?? undefined,
          planId: Number(id),
          serviceId: row?.serviceId ?? row?.service?.id ?? null,
          quantity: Number(row?.quantity) > 0 ? Number(row?.quantity) : 1,
          inconsistenceQuantity:
            Number(row?.inconsistenceQuantity) >= 0 ? Number(row?.inconsistenceQuantity) : 0,
        };

        // опционально, только если есть валидные значения
        const deptId = Number(row?.departmentId ?? row?.department?.id);
        if (Number.isFinite(deptId) && deptId > 0) payload.departmentId = deptId;
        const divId = Number(row?.divisionId ?? row?.division?.id);
        if (Number.isFinite(divId) && divId > 0) payload.divisionId = divId;
        const price = Number(row?.price);
        if (Number.isFinite(price) && price >= 0) payload.price = price;
        const wc = Number(row?.weightCoefficient);
        if (Number.isFinite(wc) && wc >= 0) payload.weightCoefficient = wc;

        if (payload.id) {
          await planServiceCRUD.edit(payload);
        } else {
          await planServiceCRUD.create(payload);
        }
      }
    },
    [id]
  );

  const loadServices = useCallback(
    async (planEntity) => {
      if (!planEntity) return;
      if (isNew) return;

      // 1) plan.indicators -> плоский список (берём groupId + indicatorName)
      const flat = deserializePlanIndicatorsToPlanIndices(
        planEntity?.indicators ?? planEntity?.Indicators
      ).filter((r) => !r?._isDeleted);
      if (!flat.length) return;

      // группируем запросы по groupId, чтобы не дергать subGroup search по каждому индикатору
      const byGroupId = new Map();
      for (const row of flat) {
        const gid = Number(row?.groupId);
        const name = String(row?.indexName || "").trim();
        if (!Number.isFinite(gid) || gid <= 0) continue;
        if (!name) continue;
        if (!byGroupId.has(gid)) byGroupId.set(gid, []);
        byGroupId.get(gid).push({ name, plannedResearch: row?.plannedResearch });
      }
      if (!byGroupId.size) return;

      // 2) load serviceSubGroups by groupId and match subGroup.name ~ indicatorName
      const matchedIndicators = [];
      for (const [gid, items] of byGroupId.entries()) {
        const resp = await serviceSubGroupCRUD.search({
          paging: { skip: 0, take: 10000, returnCount: false },
          filter: {
            groupId: { operand1: String(gid), operator: "equals" },
          },
          sort: { id: { operator: "asc", ordinal: 0 } },
        });
        const subGroups = pickRows(resp) || [];

        for (const it of items) {
          const targetNorm = normalizeIndicatorName(it.name);
          const best =
            subGroups.find((sg) => normalizeIndicatorName(sg?.name) === targetNorm) ||
            subGroups.find((sg) => {
              const sgNorm = normalizeIndicatorName(sg?.name);
              return sgNorm && (sgNorm.includes(targetNorm) || targetNorm.includes(sgNorm));
            }) ||
            null;
          matchedIndicators.push({
            groupId: gid,
            subGroupName: String(best?.name || it.name).trim(),
            plannedResearch: it.plannedResearch,
          });
        }
      }
      if (!matchedIndicators.length) return;

      // 3) как в ObjectReferralCreate: load services by indicators batch endpoint
      const indicators2 = matchedIndicators.map((x) => ({
        groupId: x.groupId,
        subGroupName: x.subGroupName,
      }));
      const services = (await serviceCRUD.post("indicators", indicators2, "services")) || [];
      const serviceList = services.filter((s) => !s?.isDeleted);
      if (!serviceList.length) return;

      // 4) сформировать PlanServices (quantity из plannedResearch)
      const qtyByServiceId = new Map();
      for (const mi of matchedIndicators) {
        const miNorm = normalizeIndicatorName(mi.subGroupName);
        const matched = serviceList.find((s) => {
          if (Number(s?.groupId) !== Number(mi.groupId)) return false;
          const name = s?.subGroup?.name ?? s?.subGroupName ?? s?.name ?? "";
          const nNorm = normalizeIndicatorName(name);
          return nNorm && (nNorm === miNorm || nNorm.includes(miNorm) || miNorm.includes(nNorm));
        });
        const sid = Number(matched?.id);
        if (!Number.isFinite(sid) || sid <= 0) continue;
        const n = Number(mi.plannedResearch);
        const add = Number.isFinite(n) && n > 0 ? n : 1;
        qtyByServiceId.set(sid, (qtyByServiceId.get(sid) || 0) + add);
      }
      const uniqServiceIds = Array.from(qtyByServiceId.keys());
      if (!uniqServiceIds.length) return;

      const generated = uniqServiceIds.map((sid) => {
        const service = serviceList.find((s) => Number(s?.id) === Number(sid)) || null;
        const prices = Array.isArray(service?.prices) ? service.prices : [];
        const firstPrice = prices[0] || null;
        const price = Number(firstPrice?.price ?? service?.price ?? 0) || 0;
        const weightCoefficient = Number(firstPrice?.costCoefficient ?? 0) || 0;
        return {
          id: null,
          planId: Number(id),
          serviceId: Number(sid),
          service,
          departmentId: service?.departmentId ?? service?.department?.id ?? null,
          department: service?.department ?? null,
          divisionId: service?.divisionId ?? service?.division?.id ?? null,
          division: service?.division ?? null,
          price,
          weightCoefficient,
          quantity: qtyByServiceId.get(Number(sid)) || 1,
          inconsistenceQuantity: 1,
        };
      });

      await persistPlanServices({ services: generated, deleteIds: [] });
      await loadPlanServices();
    },
    [id, isNew, loadPlanServices, persistPlanServices]
  );

  const hasAnyPlanServices = useMemo(() => {
    const list = planServices || [];
    if (!list.length) return false;
    return list.some((row) => {
      // если строка помечена как удалённая — она не считается “живой”
      // (для серверных удалений используется _isDeleted)
      return !row?._isDeleted;
    });
  }, [planServices]);

  const derivePlanStateId = useCallback((stateIdMaybe, stateNameMaybe) => {
    const numeric = Number(stateIdMaybe);
    if (Number.isFinite(numeric)) return numeric;
    const nRaw = String(stateNameMaybe || "")
      .toLowerCase()
      .replace(/\s+/g, " ")
      .trim();
    const n = nRaw.replace(/[.,;:()]/g, "").trim();
    if (!n) return null;
    // tolerate labels like "Новая", "Статус: Новая", etc.
    if (n.includes("новая")) return planStateIds.created;
    if (n.includes("заполня")) return planStateIds.filling;
    if (n.includes("отправлен в нцэ")) return planStateIds.sentToNce;
    if (n.includes("в работе нцэ")) return planStateIds.inProgressNce;
    if (n.includes("отправлен на согласование")) return planStateIds.sentForApproval;
    if (n.includes("на доработке")) return planStateIds.revision;
    if (n.includes("на утверждении")) return planStateIds.onApproval;
    if (n.includes("утвержд")) return planStateIds.approved;
    const map = {
      // RU labels
      "новая": planStateIds.created,
      "заполняется": planStateIds.filling,
      "отправлен в нцэ": planStateIds.sentToNce,
      "передан на обработку филиалу нцэ": planStateIds.transferredToBranch,
      "в работе нцэ": planStateIds.inProgressNce,
      "редактируется исполнителем": planStateIds.editedByExecutor,
      "на согласовании сла": planStateIds.onSlaApproval,
      "отправлен на согласование": planStateIds.sentForApproval,
      "на доработке": planStateIds.revision,
      "возвращён на исправление": planStateIds.returnedForCorrection,
      "согласован": planStateIds.agreed,
      "одобрен сла": planStateIds.slaApproved,
      "на утверждении": planStateIds.onApproval,
      "передан руководителю департамента": planStateIds.transferredToDepartmentHead,
      "утверждён": planStateIds.approved,
      "финальный статус": planStateIds.final,
      "отклонён": planStateIds.declined,
      "отказ на любом этапе": planStateIds.rejected,
      // accept english-ish codes as names
      "created": planStateIds.created,
      "senttonce": planStateIds.sentToNce,
      "inprogressnce": planStateIds.inProgressNce,
      "sentforapproval": planStateIds.sentForApproval,
      "revision": planStateIds.revision,
      "onapproval": planStateIds.onApproval,
      "approved": planStateIds.approved,
      "undefined": planStateIds.undefined,
    };
    return map[n] ?? null;
  }, []);

  const effectiveStateId = useMemo(
    () => derivePlanStateId(planMeta.stateId, planMeta.stateName),
    [derivePlanStateId, planMeta.stateId, planMeta.stateName]
  );

  // Workflow живёт на PlanDocument — на карточке строки кнопок нет.
  const workflowListForDebug = useMemo(() => [], []);
  const workflowActions = useMemo(() => [], []);

  const runWorkflowTransition = useCallback(
    async ({ oldStateId, newStateId, comment = null }) => {
      if (isNew) return;
      try {
        startWorkflow();
        await planCRUD.workflowRun(
          Number(id),
          Number(oldStateId),
          Number(newStateId),
          comment == null ? "" : String(comment)
        );
        await fetch();
        alert.show(1, "Статус изменён");
      } catch (error) {
        alert.show(0, error);
      } finally {
        stopWorkflow();
      }
    },
    [alert, fetch, id, isNew, startWorkflow, stopWorkflow]
  );

  useEffect(() => {
    if (!ncaClient) return undefined;
    const prev = ncaClient.onmessage;
    ncaClient.onmessage = (msg) => {
      const pending = ecpPendingRef.current;
      if (pending) {
        ecpPendingRef.current = null;
        const ok = msg?.code === "200" || msg?.code === 200;
        if (ok && msg?.responseObject != null) {
          pending.resolve(msg.responseObject);
        } else {
          pending.reject(
            new Error(
              msg?.message ||
                (ok ? "Пустой ответ ЭЦП" : "Ошибка подписи через NCALayer")
            )
          );
        }
        return;
      }
      if (typeof prev === "function") prev(msg);
    };
    return () => {
      ncaClient.onmessage = prev;
    };
  }, [ncaClient]);

  const buildPlanApproveSignXml = useCallback(() => {
    const payload = {
      PlanWorkflowApproval: {
        _attributes: {
          planId: String(id),
          fromStateId: String(planStateIds.onApproval),
          toStateId: String(planStateIds.approved),
          atUtc: new Date().toISOString(),
        },
      },
    };
    return js2xml(payload, { compact: true });
  }, [id]);

  const signPlanXmlWithNca = useCallback(
    (xml) =>
      new Promise((resolve, reject) => {
        if (!ncaClient?.signXml) {
          reject(
            new Error(
              "NCALayer не готов. Установите NCALayer и обновите страницу."
            )
          );
          return;
        }
        if (ecpPendingRef.current) {
          reject(new Error("Подпись уже выполняется, подождите."));
          return;
        }
        ecpPendingRef.current = { resolve, reject };
        try {
          ncaClient.signXml("PKCS12", "SIGNATURE", xml);
        } catch (e) {
          ecpPendingRef.current = null;
          reject(e);
        }
      }),
    [ncaClient]
  );

  const runWorkflowSignedTransition = useCallback(
    async ({ comment = null } = {}) => {
      if (isNew) return;
      try {
        startWorkflow();
        const xml = buildPlanApproveSignXml();
        const signedXml = await signPlanXmlWithNca(xml);
        // Как у протокола: бэкенд определяет переход по текущему статусу сущности
        await planCRUD.workflowSignRunProtocol({
          id: Number(id),
          comment: comment == null ? "" : String(comment),
          sign: { xml, signedXml },
        });
        await fetch();
        alert.show(1, "Статус изменён");
      } catch (error) {
        alert.show(0, error);
      } finally {
        stopWorkflow();
      }
    },
    [
      alert,
      buildPlanApproveSignXml,
      fetch,
      id,
      isNew,
      signPlanXmlWithNca,
      startWorkflow,
      stopWorkflow,
    ]
  );

  const isSentForApprovalReport = useMemo(() => {
    if (isNew) return false;
    return Number(planMeta.stateId) === Number(planStateIds.onSlaApproval);
  }, [isNew, planMeta.stateId]);

  const getPriceByServiceId = useCallback(async (serviceId) => {
    const numericServiceId = Number(serviceId);
    if (!Number.isFinite(numericServiceId) || numericServiceId <= 0) return 0;
    try {
      const searchResult = await servicePriceListCRUD.search({
        paging: { pageNumber: 1, pageSize: 1, returnCount: false },
        filter: {
          serviceId: { operand1: String(numericServiceId), operator: "equals" },
        },
      });
      const searchRows = pickRows(searchResult);
      if (searchRows.length > 0) {
        const price = Number(searchRows[0]?.price);
        if (Number.isFinite(price) && price > 0) return price;
      }

      const serviceData = await serviceCRUD.get(numericServiceId);
      const fallbackPrice = [serviceData?.price, serviceData?.prices?.[0]?.price]
        .map((value) => Number(value))
        .find((value) => Number.isFinite(value) && value > 0);
      return fallbackPrice || 0;
    } catch (error) {
      console.warn("Ошибка получения цены по serviceId:", error);
      return 0;
    }
  }, []);

  const getServicePrice = useCallback(async (indexId, preferredServiceId = null) => {
    // Если есть цена в Plan, используем её
    if (fields.price && Number(fields.price) > 0) {
      return Number(fields.price);
    }

    // Для выбранного показателя приоритетнее его serviceId из тарификатора
    const directServicePrice = await getPriceByServiceId(preferredServiceId);
    if (directServicePrice > 0) {
      return directServicePrice;
    }

    // Если есть serviceId, пытаемся получить цену из servicePriceList
    if (fields.serviceId) {
      try {
        // Сначала пробуем найти по priceListId, если он есть
        if (fields.priceListId) {
          const priceListData = await servicePriceListCRUD.get(fields.priceListId);
          if (priceListData?.price && Number(priceListData.price) > 0) {
            return Number(priceListData.price);
          }
        }

        // Если не нашли по priceListId, ищем по serviceId
        const searchResult = await servicePriceListCRUD.search({
          paging: { pageNumber: 1, pageSize: 1, returnCount: false },
          filter: {
            serviceId: { operand1: String(fields.serviceId), operator: "equals" },
          },
        });

        if (searchResult?.result?.length > 0) {
          const price = searchResult.result[0]?.price;
          if (price && Number(price) > 0) {
            return Number(price);
          }
        }

        // Если не нашли в servicePriceList, пробуем получить из service
        const serviceData = await serviceCRUD.get(fields.serviceId);
        if (serviceData?.price && Number(serviceData.price) > 0) {
          return Number(serviceData.price);
        }
      } catch (error) {
        console.warn("Ошибка получения цены услуги:", error);
      }
    }

    // Если в плане услуга не выбрана, пробуем взять цену по индикатору (DiseaseIndex -> services)
    if (indexId != null && indexId !== "") {
      const numericIndexId = Number(indexId);
      if (Number.isFinite(numericIndexId) && numericIndexId > 0) {
        const cached = indexPriceMap[numericIndexId];
        if (Number.isFinite(cached) && cached > 0) {
          return cached;
        }

        try {
          const diseaseIndex = await diseaseIndexCRUD.get(numericIndexId);
          const services = Array.isArray(diseaseIndex?.services) ? diseaseIndex.services : [];
          const firstService = services[0] || null;
          const directPrice = [
            firstService?.price,
            firstService?.service?.price,
            firstService?.servicePrice,
            firstService?.service?.prices?.[0]?.price,
          ]
            .map((value) => Number(value))
            .find((value) => Number.isFinite(value) && value > 0);

          if (Number.isFinite(directPrice) && directPrice > 0) {
            setIndexPriceMap((prev) => ({ ...prev, [numericIndexId]: directPrice }));
            return directPrice;
          }

          // Часто бэкенд возвращает только serviceId без раскрытого service -> догружаем услугу
          let mappedServiceId = Number(
            firstService?.serviceId ?? firstService?.service?.id ?? NaN
          );
          
          // Фолбэк: ищем service по code === serviceCode из diseaseIndex
          if ((!Number.isFinite(mappedServiceId) || mappedServiceId <= 0) && firstService?.serviceCode) {
            try {
              const byCodeResp = await serviceCRUD.search({
                paging: { skip: 0, take: 1, returnCount: false },
                filter: {
                  code: { operand1: String(firstService.serviceCode), operator: "equals" },
                },
              });
              const byCodeRows = pickRows(byCodeResp);
              if (byCodeRows.length > 0 && Number(byCodeRows[0].id) > 0) {
                mappedServiceId = Number(byCodeRows[0].id);
              }
            } catch (err) {
              console.warn("Не удалось найти service по serviceCode:", err);
            }
          }
          
          // Фолбэк 2: если diseaseIndex.services пуст, ищем через diseaseIndexServiceCRUD
          if (!Number.isFinite(mappedServiceId) || mappedServiceId <= 0) {
            try {
              const disResp = await diseaseIndexServiceCRUD.search({
                paging: { skip: 0, take: 10, returnCount: false },
                filter: {
                  indexId: { operand1: String(numericIndexId), operator: "equals" },
                },
              });
              const disRows = pickRows(disResp);
              const foundSid = disRows
                .map((item) => Number(item.serviceId))
                .find((v) => Number.isFinite(v) && v > 0);
              if (foundSid > 0) {
                mappedServiceId = foundSid;
              } else {
                const foundCode = disRows
                  .map((item) => item.serviceCode)
                  .find((v) => v != null && String(v).trim() !== "");
                if (foundCode) {
                  const byCodeResp2 = await serviceCRUD.search({
                    paging: { skip: 0, take: 1, returnCount: false },
                    filter: {
                      code: { operand1: String(foundCode), operator: "equals" },
                    },
                  });
                  const byCodeRows2 = pickRows(byCodeResp2);
                  if (byCodeRows2.length > 0 && Number(byCodeRows2[0].id) > 0) {
                    mappedServiceId = Number(byCodeRows2[0].id);
                  }
                }
              }
            } catch (err) {
              console.warn("getServicePrice: ошибка diseaseIndexServiceCRUD:", err);
            }
          }
          
          if (Number.isFinite(mappedServiceId) && mappedServiceId > 0) {
            const priceByServiceId = await getPriceByServiceId(mappedServiceId);
            if (priceByServiceId > 0) {
              setIndexPriceMap((prev) => ({ ...prev, [numericIndexId]: priceByServiceId }));
              return priceByServiceId;
            }
            const serviceData = await serviceCRUD.get(mappedServiceId);
            const servicePrice = [serviceData?.price, serviceData?.prices?.[0]?.price]
              .map((value) => Number(value))
              .find((value) => Number.isFinite(value) && value > 0);
            if (Number.isFinite(servicePrice) && servicePrice > 0) {
              setIndexPriceMap((prev) => ({ ...prev, [numericIndexId]: servicePrice }));
              return servicePrice;
            }
          }
        } catch (error) {
          console.warn("Ошибка получения цены по индикатору:", error);
        }
      }
    }

    return 0;
  }, [fields.price, fields.serviceId, fields.priceListId, getPriceByServiceId, indexPriceMap]);

  const resolveRowIndexId = useCallback(
    async (row) => {
      const directId = Number(row?.indexId);
      if (Number.isFinite(directId) && directId > 0) {
        return directId;
      }

      const normalizedRowName = normalizeIndicatorName(row?.indexName);
      if (!normalizedRowName) return null;
      const cachedId = Number(indexNameIdMap[normalizedRowName]);
      if (Number.isFinite(cachedId) && cachedId > 0) {
        return cachedId;
      }

      const pool = (diseaseIndices || []).map((item) => ({
        id: Number(item.id),
        normalized: normalizeIndicatorName(item.name),
      }));

      const exact = pool.find((item) => item.normalized === normalizedRowName);
      if (exact?.id) {
        setIndexNameIdMap((prev) => ({ ...prev, [normalizedRowName]: Number(exact.id) }));
        return exact.id;
      }

      const fuzzy = pool.find(
        (item) =>
          item.normalized.includes(normalizedRowName) ||
          normalizedRowName.includes(item.normalized)
      );
      if (fuzzy?.id) {
        setIndexNameIdMap((prev) => ({ ...prev, [normalizedRowName]: Number(fuzzy.id) }));
        return fuzzy.id;
      }

      // Фолбэк: ищем на сервере по имени индикатора
      try {
        const byNameResponse = await diseaseIndexCRUD.search({
          paging: { skip: 0, take: 50, returnCount: false },
          filter: {
            name: {
              operand1: String(row?.indexName || "").trim(),
              operator: "like",
            },
          },
          sort: { id: { operator: "asc", ordinal: 0 } },
        });
        const candidates = pickRows(byNameResponse);
        const normalizedCandidates = candidates.map((item) => ({
          id: Number(item.id),
          normalized: normalizeIndicatorName(item.name),
        }));
        const exactRemote =
          normalizedCandidates.find((item) => item.normalized === normalizedRowName) ||
          normalizedCandidates.find(
            (item) =>
              item.normalized.includes(normalizedRowName) ||
              normalizedRowName.includes(item.normalized)
          );
        if (exactRemote?.id) {
          setIndexNameIdMap((prev) => ({ ...prev, [normalizedRowName]: Number(exactRemote.id) }));
          return Number(exactRemote.id);
        }

        // Если нормализация не дала совпадение, но сервер вернул кандидатов —
        // берём первый (это лучше, чем null, иначе услуги не сформируются вообще).
        const firstCandidateId = Number(candidates?.[0]?.id);
        if (Number.isFinite(firstCandidateId) && firstCandidateId > 0) {
          setIndexNameIdMap((prev) => ({ ...prev, [normalizedRowName]: firstCandidateId }));
          return firstCandidateId;
        }
      } catch (error) {
        console.warn("Не удалось найти indexId по имени индикатора:", error);
      }

      return null;
    },
    [diseaseIndexCRUD, diseaseIndices, indexNameIdMap]
  );

  const buildPlanServicesFromPlanIndices = useCallback(async () => {
    const indices = (planIndices || []).filter((row) => !row?._isDeleted);
    if (!indices.length) return [];

    // 1) planIndex -> indexId (батч: сначала собираем все indexId)
    const resolvedIndexIds = await Promise.all(
      indices.map(async (row) => {
        const indexId = Number(await resolveRowIndexId(row));
        return Number.isFinite(indexId) && indexId > 0 ? indexId : null;
      })
    );
    const uniqIndexIds = Array.from(new Set(resolvedIndexIds.filter(Boolean)));
    if (!uniqIndexIds.length) return [];

    // 2) indexId[] -> diseaseIndex[] (батч загрузка: параллельные get)
    const diseaseIndexPairs = await Promise.all(
      uniqIndexIds.map(async (idxId) => {
        try {
          const entity = await diseaseIndexCRUD.get(idxId);
          return [idxId, entity || null];
        } catch {
          return [idxId, null];
        }
      })
    );
    const diseaseIndexById = new Map(diseaseIndexPairs);

    // 3) diseaseIndex.services[].serviceId -> serviceId[] (и сразу считаем quantity)
    const plannedQtyByServiceId = new Map(); // serviceId -> sum(plannedResearch)
    const addQty = (sid, qty) => {
      const n = Number(qty);
      const add = Number.isFinite(n) && n > 0 ? n : 1;
      plannedQtyByServiceId.set(Number(sid), (plannedQtyByServiceId.get(Number(sid)) || 0) + add);
    };

    indices.forEach((row, i) => {
      const indexId = resolvedIndexIds[i];
      if (!indexId) return;
      const di = diseaseIndexById.get(indexId);
      if (!di) return;

      // Если выбран один код исследования (serviceId) — генерируем ПланServices только под него.
      const selectedSid = Number(row?.serviceId ?? 0);
      if (Number.isFinite(selectedSid) && selectedSid > 0) {
        addQty(selectedSid, row?.plannedResearch);
        return;
      }

      const serviceIds = [];
      const directSid = Number(di?.serviceId ?? di?.ServiceId ?? di?.service?.id);
      if (Number.isFinite(directSid) && directSid > 0) serviceIds.push(directSid);

      const arr = Array.isArray(di?.services) ? di.services : [];
      for (const s of arr) {
        const sid = Number(s?.serviceId ?? s?.service?.id);
        if (Number.isFinite(sid) && sid > 0) serviceIds.push(sid);
      }

      Array.from(new Set(serviceIds)).forEach((sid) => addQty(sid, row?.plannedResearch));
    });

    const uniqServiceIds = Array.from(plannedQtyByServiceId.keys()).filter(
      (x) => Number.isFinite(Number(x)) && Number(x) > 0
    );
    if (!uniqServiceIds.length) return [];

    // 4) serviceId[] -> services[] (батч загрузка: параллельные get)
    const services = await Promise.all(
      uniqServiceIds.map(async (sid) => {
        try {
          const s = await serviceCRUD.get(sid);
          return { sid, service: s };
        } catch {
          return { sid, service: null };
        }
      })
    );

    return services
      .filter((x) => Number.isFinite(Number(x.sid)) && Number(x.sid) > 0)
      .map(({ sid, service }) => {
        const prices = Array.isArray(service?.prices) ? service.prices : [];
        const firstPrice = prices[0] || null;
        const price = Number(firstPrice?.price ?? service?.price ?? 0) || 0;
        const weightCoefficient = Number(firstPrice?.costCoefficient ?? 0) || 0;

        return {
          id: null,
          planId: Number(id),
          serviceId: Number(sid),
          service,
          departmentId: service?.departmentId ?? service?.department?.id ?? null,
          department: service?.department ?? null,
          divisionId: service?.divisionId ?? service?.division?.id ?? null,
          division: service?.division ?? null,
          price,
          weightCoefficient,
          quantity: plannedQtyByServiceId.get(Number(sid)) || 1,
          inconsistenceQuantity: 1,
        };
      });
  }, [diseaseIndexCRUD, id, planIndices, resolveRowIndexId, serviceCRUD]);

  const ensurePlanServicesForSendForApproval = useCallback(async () => {
    if (hasAnyPlanServices) return true;
    try {
      startServices();
      const generated = await buildPlanServicesFromPlanIndices();
      if (!generated.length) {
        alert.show(0, "Не удалось сформировать услуги: в плане нет индексов/услуг для отправки");
        return false;
      }
      // сохраняем на сервер (без зависимости от setState)
      await persistPlanServices({ services: generated, deleteIds: [] });
      await loadPlanServices();
      setServicesAutoGenerated(true);
      return true;
    } catch (e) {
      alert.show(0, e);
      return false;
    } finally {
      stopServices();
    }
  }, [
    alert,
    buildPlanServicesFromPlanIndices,
    hasAnyPlanServices,
    loadPlanServices,
    persistPlanServices,
    startServices,
    stopServices,
  ]);

  useEffect(() => {
    // Автогенерация (как в постановлении): на этапе НЦЭ, если услуг ещё нет —
    // сформировать из индексов и сохранить. Без ошибок/тостов (они только при клике "Отправить").
    if (!isNceExecutionStage) return;
    if (hasAnyPlanServices) return;
    if (!(planIndices || []).some((x) => !x?._isDeleted)) return;
    if (servicesGenerationTried) return;

    let cancelled = false;
    (async () => {
      try {
        setServicesGenerationTried(true);
        const generated = await buildPlanServicesFromPlanIndices();
        if (cancelled) return;
        if (!generated || generated.length === 0) return;

        await persistPlanServices({ services: generated, deleteIds: [] });
        if (cancelled) return;
        await loadPlanServices();
        if (cancelled) return;
        setServicesAutoGenerated(true);
      } catch (e) {
        // не молчим: иначе кажется "пустота" без причин
        console.warn("[PlanServices] автогенерация не удалась:", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    buildPlanServicesFromPlanIndices,
    hasAnyPlanServices,
    isNceExecutionStage,
    loadPlanServices,
    persistPlanServices,
    planIndices,
    servicesGenerationTried,
  ]);

  const updatePlanIndexField = useCallback(async (idx, key, value) => {
    const numValue = parseNumber(value);
    
    // НЦЭ этап: выбираем один код исследования => serviceId,
    // затем пересчитываем План (сумма) как План (иссл.) * цена выбранного кода.
    if (key === "serviceId") {
      const row = planIndices[idx];
      if (!row) return;

      const nextServiceId =
        Number.isFinite(Number(value)) && Number(value) > 0 ? Number(value) : null;

      let price = 0;
      if (nextServiceId != null) {
        price = await getPriceByServiceId(nextServiceId);
      }

      const hasPrice = Number(price) > 0;
      const qty = Number(row.plannedResearch) || 0;
      const calculatedSum = hasPrice ? qty * price : 0;

      setPlanIndices((prev) =>
        prev.map((item, itemIdx) =>
          itemIdx === idx
            ? {
                ...item,
                serviceId: nextServiceId,
                plannedSum: calculatedSum,
                _manualSum: !hasPrice,
              }
            : item
        )
      );

      return;
    }

    // Если изменился plannedResearch, автоматически рассчитываем plannedSum
    if (key === "plannedResearch") {
      const row = planIndices[idx];
      if (!row) return;
      
      console.log("=== [PlanEdit] updatePlanIndexField ===");
      console.log("Все строки planIndices:", JSON.stringify(planIndices, null, 2));
      console.log("Текущая строка idx:", idx);
      console.log("Индикатор:", row?.indexName, "| indexId:", row?.indexId, "| serviceId из строки:", row?.serviceId);
      
      let price = 0;
      let resolvedServiceId = row?.serviceId;
      
      // Если serviceId нет в строке, пробуем найти его по indexId
      if (!resolvedServiceId || Number(resolvedServiceId) <= 0) {
        const resolvedIndexId = await resolveRowIndexId(row);
        console.log("resolvedIndexId:", resolvedIndexId);
        if (resolvedIndexId && Number(resolvedIndexId) > 0) {
          try {
            const diseaseIndex = await diseaseIndexCRUD.get(resolvedIndexId);
            const services = Array.isArray(diseaseIndex?.services) ? diseaseIndex.services : [];
            console.log("diseaseIndex.services:", JSON.stringify(services));
            const firstService = services[0] || null;
            resolvedServiceId = Number(
              firstService?.serviceId ?? firstService?.service?.id ?? 0
            );
            console.log("serviceId из diseaseIndex.services:", resolvedServiceId, "| serviceCode:", firstService?.serviceCode);
            
            // Фолбэк: ищем service по code === diseaseIndex.serviceCode
            if ((!resolvedServiceId || resolvedServiceId <= 0) && firstService?.serviceCode) {
              try {
                console.log("Ищем service по code:", firstService.serviceCode);
                const byCodeResp = await serviceCRUD.search({
                  paging: { skip: 0, take: 1, returnCount: false },
                  filter: {
                    code: { operand1: String(firstService.serviceCode), operator: "equals" },
                  },
                });
                const byCodeRows = pickRows(byCodeResp);
                console.log("Результат поиска service по code:", JSON.stringify(byCodeRows));
                if (byCodeRows.length > 0 && Number(byCodeRows[0].id) > 0) {
                  resolvedServiceId = Number(byCodeRows[0].id);
                  console.log("Найден serviceId по code:", resolvedServiceId);
                }
              } catch (err) {
                console.warn("Не удалось найти service по serviceCode:", err);
              }
            }
            
            // Фолбэк 2: если diseaseIndex.services пуст, ищем через diseaseIndexServiceCRUD
            if (!resolvedServiceId || resolvedServiceId <= 0) {
              try {
                console.log("diseaseIndex.services пуст, ищем через diseaseIndexServiceCRUD по indexId:", resolvedIndexId);
                const disResp = await diseaseIndexServiceCRUD.search({
                  paging: { skip: 0, take: 10, returnCount: false },
                  filter: {
                    indexId: { operand1: String(resolvedIndexId), operator: "equals" },
                  },
                });
                const disRows = pickRows(disResp);
                console.log("diseaseIndexServiceCRUD результат:", JSON.stringify(disRows));
                const foundServiceId = disRows
                  .map((item) => Number(item.serviceId))
                  .find((v) => Number.isFinite(v) && v > 0);
                if (foundServiceId > 0) {
                  resolvedServiceId = foundServiceId;
                  console.log("Найден serviceId через diseaseIndexServiceCRUD:", resolvedServiceId);
                }
                
                // Если и тут пусто, пробуем по serviceCode из diseaseIndexServiceCRUD
                if (!resolvedServiceId || resolvedServiceId <= 0) {
                  const foundServiceCode = disRows
                    .map((item) => item.serviceCode)
                    .find((v) => v != null && String(v).trim() !== "");
                  if (foundServiceCode) {
                    console.log("Ищем service по code из diseaseIndexService:", foundServiceCode);
                    const byCodeResp2 = await serviceCRUD.search({
                      paging: { skip: 0, take: 1, returnCount: false },
                      filter: {
                        code: { operand1: String(foundServiceCode), operator: "equals" },
                      },
                    });
                    const byCodeRows2 = pickRows(byCodeResp2);
                    if (byCodeRows2.length > 0 && Number(byCodeRows2[0].id) > 0) {
                      resolvedServiceId = Number(byCodeRows2[0].id);
                      console.log("Найден serviceId по code:", resolvedServiceId);
                    }
                  }
                }
              } catch (err) {
                console.warn("Ошибка поиска через diseaseIndexServiceCRUD:", err);
              }
            }
            
            // Сохраняем найденный serviceId в строку для будущих расчётов
            if (resolvedServiceId > 0) {
              console.log("Сохраняем serviceId в строку:", resolvedServiceId);
              setPlanIndices((prev) =>
                prev.map((item, itemIdx) =>
                  itemIdx === idx ? { ...item, serviceId: resolvedServiceId } : item
                )
              );
            }
          } catch (error) {
            console.warn("Не удалось найти serviceId по indexId:", error);
          }
        }
      }
      
      // Пробуем получить цену по serviceId
      if (resolvedServiceId && Number(resolvedServiceId) > 0) {
        console.log("Получаем цену по serviceId:", resolvedServiceId);
        price = await getPriceByServiceId(resolvedServiceId);
        console.log("Цена по serviceId:", price);
      }
      
      // Если не нашли, пробуем по indexId через getServicePrice
      if (price <= 0) {
        const resolvedIndexId = await resolveRowIndexId(row);
        console.log("Фолбэк getServicePrice, indexId:", resolvedIndexId);
        price = await getServicePrice(resolvedIndexId, resolvedServiceId);
        console.log("Цена из getServicePrice:", price);
      }
      
      const hasPrice = Number(price) > 0;
      const calculatedSum = hasPrice ? numValue * price : 0;
      console.log("Итого: кол-во =", numValue, "× цена =", price, "= сумма", calculatedSum, "| ручной ввод:", !hasPrice);
      console.log("=== конец расчёта ===");
      
      setPlanIndices((prev) =>
        prev.map((item, itemIdx) =>
          itemIdx === idx
            ? {
                ...item,
                [key]: numValue,
                plannedSum: hasPrice ? calculatedSum : item.plannedSum,
                _manualSum: !hasPrice,
              }
            : item
        )
      );
    } else {
      setPlanIndices((prev) =>
        prev.map((item, itemIdx) =>
          itemIdx === idx ? { ...item, [key]: numValue } : item
        )
      );
    }
  }, [getServicePrice, getPriceByServiceId, planIndices, resolveRowIndexId]);

  const addIndicatorsToPlan = useCallback((items = [], meta = {}) => {
    const {
      departmentId = null,
      departmentName = null,
      groupId = null,
      groupName = null,
    } = meta;
    const deptPart =
      departmentId != null && String(departmentId).trim() !== ""
        ? String(departmentId)
        : "_";
    const grpPart =
      groupId != null && String(groupId).trim() !== ""
        ? String(groupId)
        : "_";
    setPlanIndices((prev) => {
      const existing = new Set(
        prev
          .filter((row) => !row._isDeleted)
          .map((row) => {
            const d =
              row.departmentId != null && String(row.departmentId).trim() !== ""
                ? String(row.departmentId)
                : "_";
            const g =
              row.groupId != null && String(row.groupId).trim() !== ""
                ? String(row.groupId)
                : "_";
            return row.indexId === "" || row.indexId == null
              ? `name:${String(row.indexName || "").toLowerCase()}|dept:${d}|grp:${g}`
              : `id:${Number(row.indexId)}|dept:${d}|grp:${g}`;
          })
      );
      const existingInSection = prev.find((row) => {
        if (row._isDeleted) return false;
        const d =
          row.departmentId != null && String(row.departmentId).trim() !== ""
            ? String(row.departmentId)
            : "_";
        const g =
          row.groupId != null && String(row.groupId).trim() !== ""
            ? String(row.groupId)
            : "_";
        return d === deptPart && g === grpPart;
      });
      const sectionSampleCount =
        existingInSection?.plannedPlan != null && existingInSection.plannedPlan !== ""
          ? Number(existingInSection.plannedPlan)
          : 1;

      const toAdd = items
        .filter((item) => {
          const itemKey =
            Number(item.id) >= SYNTHETIC_INDEX_OFFSET
              ? `name:${String(item.name || "").toLowerCase()}|dept:${deptPart}|grp:${grpPart}`
              : `id:${Number(item.id)}|dept:${deptPart}|grp:${grpPart}`;
          return !existing.has(itemKey);
        })
        .map((item) => ({
          ...createEmptyPlanIndex(),
          plannedPlan: Number.isFinite(sectionSampleCount) ? sectionSampleCount : 1,
          indexId: Number(item.id) >= SYNTHETIC_INDEX_OFFSET ? "" : Number(item.id),
          indexName: item.name || `Индекс ${item.id}`,
          serviceId: Number(item.serviceId) > 0 ? Number(item.serviceId) : null,
          subGroupId: Number(item.subGroupId) > 0 ? Number(item.subGroupId) : null,
          departmentId:
            departmentId != null && String(departmentId).trim() !== ""
              ? Number(departmentId)
              : null,
          departmentName: departmentName || null,
          groupId:
            groupId != null && String(groupId).trim() !== ""
              ? Number(groupId)
              : null,
          groupName: groupName || null,
        }));
      return [...prev, ...toAdd];
    });
  }, []);

  const removePlanRowForGroupIndicator = useCallback((group, item) => {
    setPlanIndices((prev) =>
      prev.flatMap((row) => {
        if (row._isDeleted) return [row];
        if (!rowMatchesPlanGroupIndicator(row, group, item)) return [row];
        if (!row.id) return [];
        return [{ ...row, _isDeleted: true }];
      })
    );
  }, []);

  const removePlanRowsForGroupIndicatorList = useCallback((group, indicatorList) => {
    setPlanIndices((prev) =>
      prev.flatMap((row) => {
        if (row._isDeleted) return [row];
        const match = indicatorList.some((ind) =>
          rowMatchesPlanGroupIndicator(row, group, ind)
        );
        if (!match) return [row];
        if (!row.id) return [];
        return [{ ...row, _isDeleted: true }];
      })
    );
  }, []);

  const updateIndicatorGroup = useCallback((groupKey, patch) => {
    setIndicatorGroups((prev) =>
      prev.map((group) =>
        group.key === groupKey
          ? { ...group, ...(typeof patch === "function" ? patch(group) : patch) }
          : group
      )
    );
  }, []);

  const addIndicatorGroup = () => {
    setIndicatorGroups((prev) => [...prev, createIndicatorGroup()]);
  };

  const removeIndicatorGroup = (groupKey) => {
    setIndicatorGroups((prev) => prev.filter((group) => group.key !== groupKey));
  };

  const loadGroupIndicators = useCallback(
    async (groupKey) => {
      const currentGroup = indicatorGroups.find((group) => group.key === groupKey);
      if (!currentGroup?.departmentId || !currentGroup?.groupId) {
        alert.show(0, "Выберите лабораторию и группу");
        return;
      }

      try {
        updateIndicatorGroup(groupKey, { loading: true, indicators: [] });

        const serviceMapsResponse = await serviceMapCRUD.search({
          paging: { skip: 0, take: 10000, returnCount: true },
          filter: {
            departmentId: {
              operand1: String(currentGroup.departmentId),
              operator: "equals",
            },
            groupId: {
              operand1: String(currentGroup.groupId),
              operator: "equals",
            },
          },
        });
        const serviceMapsPrimary = pickRows(serviceMapsResponse);
        const serviceMapsFallbackResponse =
          serviceMapsPrimary.length > 0
            ? serviceMapsResponse
            : await serviceMapCRUD.search({
                paging: { skip: 0, take: 10000, returnCount: true },
                filter: {
                  groupId: {
                    operand1: String(currentGroup.groupId),
                    operator: "equals",
                  },
                },
              });
        const serviceMapsRows = pickRows(serviceMapsFallbackResponse);

        const serviceIds = Array.from(
          new Set(
            serviceMapsRows
              .map((item) => Number(item.serviceId ?? item.service?.id))
              .filter((value) => Number.isFinite(value) && value > 0)
          )
        );

        const mappings = serviceIds.length
          ? await Promise.all(
              serviceIds.map(async (serviceId) => {
                const response = await diseaseIndexServiceCRUD.search({
                  paging: { skip: 0, take: 10000, returnCount: false },
                  filter: {
                    serviceId: {
                      operand1: String(serviceId),
                      operator: "equals",
                    },
                  },
                });
                return pickRows(response);
              })
            )
          : [];

        const flatMappings = mappings.flat();
        const indexToServiceMap = new Map();
        flatMappings.forEach((item) => {
          const idx = Number(item.indexId);
          const sid = Number(item.serviceId);
          if (!Number.isFinite(idx) || idx <= 0 || !Number.isFinite(sid) || sid <= 0) return;
          if (!indexToServiceMap.has(idx)) {
            indexToServiceMap.set(idx, sid);
          }
        });

        const indexIds = Array.from(
          new Set(
            flatMappings
              .map((item) => Number(item.indexId))
              .filter((value) => Number.isFinite(value) && value > 0)
          )
        );

        const knownById = new Map(
          (diseaseIndices || []).map((item) => [Number(item.id), item.name || `Индекс ${item.id}`])
        );
        const diseasePool = (diseaseIndices || []).map((item) => ({
          id: Number(item.id),
          name: item.name || `Индекс ${item.id}`,
          normalized: normalizeIndicatorName(item.name),
        }));

        const missingIds = indexIds.filter((indexId) => !knownById.has(Number(indexId)));
        if (missingIds.length) {
          const missingItems = await Promise.all(
            missingIds.map(async (indexId) => {
              try {
                const item = await diseaseIndexCRUD.get(indexId);
                return { id: Number(indexId), name: item?.name || `Индекс ${indexId}` };
              } catch (error) {
                return { id: Number(indexId), name: `Индекс ${indexId}` };
              }
            })
          );
          missingItems.forEach((item) => knownById.set(Number(item.id), item.name));
        }

        let indicators = indexIds
          .map((indexId) => ({
            id: Number(indexId),
            name: knownById.get(Number(indexId)),
            serviceId: indexToServiceMap.get(Number(indexId)) || null,
          }))
          .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "ru"));

        if (!indicators.length) {
          // Фолбэк как в предписании: через serviceMap -> subGroup -> название показателя
          const subGroupIds = Array.from(
            new Set(
              serviceMapsRows
                .map((item) => Number(item.subGroupId))
                .filter((value) => Number.isFinite(value) && value > 0)
            )
          );
          if (subGroupIds.length) {
            const subGroupsResponse = await serviceSubGroupCRUD.search({
              paging: { skip: 0, take: 10000, returnCount: false },
            });
            const subGroupsById = new Map(
              pickRows(subGroupsResponse).map((item) => [Number(item.id), item.name || ""])
            );
            indicators = subGroupIds
              .map((subGroupId) => ({
                subGroupId: Number(subGroupId),
                name: String(subGroupsById.get(Number(subGroupId)) || "").trim(),
              }))
              .filter(Boolean)
              .map((item) => {
                const normalized = normalizeIndicatorName(item.name);
                const matched =
                  diseasePool.find((candidate) => candidate.normalized === normalized) ||
                  diseasePool.find(
                    (candidate) =>
                      candidate.normalized.includes(normalized) ||
                      normalized.includes(candidate.normalized)
                  );
                const matchedServiceId = serviceMapsRows.find(
                  (mapItem) => Number(mapItem.subGroupId) === Number(item.subGroupId)
                )?.serviceId;
                if (matched) {
                  return {
                    id: Number(matched.id),
                    name: matched.name,
                    serviceId: Number(matchedServiceId) > 0 ? Number(matchedServiceId) : null,
                  };
                }
                return {
                  id: SYNTHETIC_INDEX_OFFSET + Number(item.subGroupId),
                  name: item.name,
                  serviceId: Number(matchedServiceId) > 0 ? Number(matchedServiceId) : null,
                };
              })
              .filter((item) => Boolean(item?.name))
              .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "ru"));
          }
        }

        if (!indicators.length) {
          indicators = (diseaseIndices || [])
            .map((item) => ({
              id: Number(item.id),
              name: item.name || `Индекс ${item.id}`,
              serviceId: null,
            }))
            .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "ru"));
        }

        updateIndicatorGroup(groupKey, { loading: false, indicators });
      } catch (error) {
        updateIndicatorGroup(groupKey, { loading: false });
        alert.show(0, error);
      }
    },
    [alert, diseaseIndices, indicatorGroups, updateIndicatorGroup]
  );

  const isGroupIndicatorInPlan = useCallback(
    (group, item) =>
      planIndices.some(
        (row) =>
          !row._isDeleted && rowMatchesPlanGroupIndicator(row, group, item)
      ),
    [planIndices]
  );

  const toggleGroupIndicator = useCallback(
    (groupKey, item, checked) => {
      const group = indicatorGroups.find((g) => g.key === groupKey);
      if (!group) return;
      if (checked) {
        addIndicatorsToPlan([item], {
          departmentId: group.departmentId,
          departmentName: group.departmentName,
          groupId: group.groupId,
          groupName: group.groupName,
        });
      } else {
        removePlanRowForGroupIndicator(group, item);
      }
    },
    [indicatorGroups, addIndicatorsToPlan, removePlanRowForGroupIndicator]
  );

  const setSelectedForAllInGroup = useCallback(
    (groupKey, checked) => {
      const group = indicatorGroups.find((item) => item.key === groupKey);
      if (!group) return;
      const list = group.indicators || [];
      if (checked) {
        addIndicatorsToPlan(list, {
          departmentId: group.departmentId,
          departmentName: group.departmentName,
          groupId: group.groupId,
          groupName: group.groupName,
        });
      } else {
        removePlanRowsForGroupIndicatorList(group, list);
      }
    },
    [indicatorGroups, addIndicatorsToPlan, removePlanRowsForGroupIndicatorList]
  );

  const removePlanIndex = (idx) => {
    setPlanIndices((prev) =>
      prev.flatMap((item, itemIdx) => {
        if (itemIdx !== idx) return [item];
        if (!item.id) return [];
        return [{ ...item, _isDeleted: true }];
      })
    );
  };

  /** Количество проб на уровне лаборатории/группы — пишется в plannedPlan всех строк секции. */
  const updateSectionSampleCount = useCallback((groupKey, value) => {
    const numValue = value === "" || value == null ? 1 : Number(value);
    setPlanIndices((prev) =>
      prev.map((row) =>
        !row._isDeleted && planIndexSectionKey(row) === groupKey
          ? { ...row, plannedPlan: Number.isFinite(numValue) ? numValue : 1 }
          : row
      )
    );
  }, []);

    return (
        <DashboardLayout>
      {loading ? (
        <CircularProgress />
      ) : (
        <>
                <Grid item xs={12}>
                    <Paper>
              <Toolbar
                style={{
                            display: "flex",
                            justifyContent: "space-between",
                }}
              >
                <Typography variant="h5" component="h2">
                  {isNew ? "Создание" : "Редактирование"} объекта плана
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={2}>
                  <NavLink style={{ color: "#2f9e69" }} to={backRoute}>
                    <span style={{ color: "2f9e69" }}>Вернуться к плану</span>
                  </NavLink>
                            </Stack>
                        </Toolbar>
                    </Paper>
                </Grid>

                {!isNew && (
                  <Grid item xs={12} style={{ paddingTop: 12 }}>
                    <Paper style={{ padding: 12 }}>
                      <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={2}
                        alignItems={{ xs: "flex-start", md: "center" }}
                        justifyContent="space-between"
                      >
                        <Typography variant="subtitle1">
                          Статус:{" "}
                          <b>
                            {planMeta.stateName ||
                              getPlanStateLabel(planMeta.stateId, "—")}
                          </b>
                          <Typography
                            component="span"
                            variant="caption"
                            color="textSecondary"
                            style={{ marginLeft: 8 }}
                          >

                          </Typography>
                        </Typography>

                        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                          {isSentForApprovalReport && (
                            <>
                              <Typography variant="body2" color="textSecondary">
                                Отчёт по кодам исследований (статус «
                                {getPlanStateLabel(planStateIds.onSlaApproval)}»):
                              </Typography>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() =>
                                  downloadPlanCodesAgreementReportXlsx({
                                    planId: id,
                                    fields,
                                    planMeta,
                                    planIndices,
                                    serviceByIdCache,
                                    planServices,
                                  })
                                }
                              >
                                Скачать Excel
                              </Button>
                            </>
                          )}
                          {(!review || canEditNceIndices) && (
                            <LoadingButton
                              size="small"
                              loading={loadingSubmit}
                              variant="outlined"
                              onClick={saveOnly}
                            >
                              Сохранить
                            </LoadingButton>
                          )}
                          {workflowActions.length === 0 && (
                            <Typography variant="body2" color="textSecondary">

                            </Typography>
                          )}
                          {workflowActions.map((action) => (
                            <LoadingButton
                              key={action.key}
                              size="small"
                              loading={workflowLoading}
                              variant="contained"
                              disabled={!action.allowed || workflowLoading}
                              title={
                                action.allowed
                                  ? ""
                                  : "Недостаточно прав для выполнения действия"
                              }
                              onClick={() => {
                                if (!action.allowed) return;
                                const [oldStateId, newStateId] = action.transition;
                                const { title, message } = action.confirm || {};

                                if (action.requiresEcpSign) {
                                  const runSigned = (comment) => {
                                    void runWorkflowSignedTransition({
                                      comment: comment ?? null,
                                    });
                                  };
                                  if (action.needsComment) {
                                    commentDialog(
                                      title || "Комментарий",
                                      message || "Укажите комментарий",
                                      ({ comment }) => runSigned(comment)
                                    );
                                    return;
                                  }
                                  confirmDialog(
                                    title || "Подтверждение",
                                    message || "Выполнить действие?",
                                    () => runSigned(null)
                                  );
                                  return;
                                }

                                const doRun = (comment) =>
                                  (async () => {
                                    // Перед 5→8 автосинхронизируем PlanServices, если пусто
                                    if (Number(oldStateId) === 5 && Number(newStateId) === 8) {
                                      // Сохраняем выбранные коды исследований (serviceId) в JSON indicators
                                      // до перехода на согласование, чтобы бэк видел актуальные данные.
                                      await persistIndicatorsJson();
                                      const ok = await ensurePlanServicesForSendForApproval();
                                      if (!ok) return;
                                    }
                                    await runWorkflowTransition({
                                      oldStateId,
                                      newStateId,
                                      comment: comment ?? null,
                                    });
                                  })();

                                if (action.needsComment) {
                                  commentDialog(
                                    title || "Комментарий",
                                    message || "Укажите комментарий",
                                    ({ comment }) => doRun(comment)
                                  );
                                  return;
                                }

                                confirmDialog(
                                  title || "Подтверждение",
                                  message || "Выполнить действие?",
                                  () => doRun(null)
                                );
                              }}
                            >
                              {action.title}
                            </LoadingButton>
                          ))}
                        </Stack>
                      </Stack>
                    </Paper>
                  </Grid>
                )}

                <Grid item xs={12} spacing={1} style={{ paddingTop: 16 }}>
                    <Paper style={{ paddingBottom: 20 }}>
                        <Grid container xs={12} style={{ paddingLeft: 20, paddingRight: 20 }}>
                            <Grid item xs={12} marginTop={2}>
                  <Typography variant="h6">Дополнительные поля</Typography>
                            </Grid>

                {PLAN_TEXT_FIELDS.map((item) => (
                  <Grid item xs={12} key={item.key} style={{ paddingTop: 12 }}>
                    <TextField
                      fullWidth
                                    disabled={review}
                      label={item.label}
                      value={fields[item.key] || ""}
                      onChange={(event) => {
                        const value = event.target.value;
                        setFields((state) => ({ ...state, [item.key]: value }));
                                    }}
                                />
                            </Grid>
                ))}

                {/* Дата первичной регистрации убрана по требованию */}

                {showIndicesSection && (
                  <Grid item xs={12} marginTop={3}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="h6">Индексы плана</Typography>
                      {!review && (
                        <LoadingButton
                          variant="outlined"
                          onClick={addIndicatorGroup}
                          disabled={isIndicesReadOnly}
                        >
                          Добавить группу
                        </LoadingButton>
                      )}
                    </Stack>
                  </Grid>
                )}

                {showIndicesSection && !isNew &&
                  <Grid item xs={12} style={{ paddingTop: 8 }}>
                    {(indicatorGroups || []).map((group, groupIndex) => (
                      <div
                        key={group.key}
                        style={{
                          border: "1px solid #d9d9d9",
                          borderRadius: 8,
                          padding: 12,
                          marginBottom: 12,
                        }}
                      >
                        <Grid container spacing={1}>
                          <Grid item xs={12} md={5}>
                            <RestAutoComplete
                              restApi={departmentCRUD}
                              id={`department_${group.key}`}
                              label="Лаборатория"
                              displayEndAdornment={false}
                              disabled={isIndicesReadOnly}
                              value={group.departmentId}
                              query={{
                                paging: { skip: 0, take: 200 },
                                sort: { name: { operator: "asc" } },
                                filter: { isDeleted: { operand1: false, operator: "equals" } },
                              }}
                              onChange={(obj) =>
                                updateIndicatorGroup(group.key, {
                                  departmentId: obj?.id || null,
                                  departmentName: obj?.name ?? null,
                                  groupId: null,
                                  groupName: null,
                                  indicators: [],
                                })
                                    }
                                />
                            </Grid>
                          <Grid item xs={12} md={5}>
                            <RestAutoComplete
                              restApi={serviceGroupCRUD}
                              id={`group_${group.key}`}
                              label="Группа"
                              displayEndAdornment={false}
                              value={group.groupId}
                              disabled={isIndicesReadOnly || !group.departmentId}
                              query={{
                                paging: { skip: 0, take: 200 },
                                sort: { name: { operator: "asc" } },
                              }}
                              onSearch={async (request, getFunc) => {
                                const response = await getFunc();
                                if (!group.departmentId) return response;

                                const mapResponse = await serviceMapCRUD.search({
                                  paging: { skip: 0, take: 10000, returnCount: false },
                                  filter: {
                                    departmentId: {
                                      operand1: String(group.departmentId),
                                      operator: "equals",
                                    },
                                  },
                                });
                                const allowedGroupIds = new Set(
                                  pickRows(mapResponse).map((item) => Number(item.groupId))
                                );
                                return {
                                  ...response,
                                  result: (response?.result || []).filter((item) =>
                                    allowedGroupIds.has(Number(item.id))
                                  ),
                                };
                              }}
                              onChange={(obj) =>
                                updateIndicatorGroup(group.key, {
                                  groupId: obj?.id || null,
                                  groupName: obj?.name ?? null,
                                  indicators: [],
                                })
                                    }
                                />
                        </Grid>
                          <Grid item xs={12} md={2} style={{ display: "flex", alignItems: "center" }}>
                            <LoadingButton
                              fullWidth
                              variant="contained"
                              loading={group.loading}
                              disabled={isIndicesReadOnly}
                              onClick={() => loadGroupIndicators(group.key)}
                            >
                              Поиск
                            </LoadingButton>
                        </Grid>
                        </Grid>

                        <Stack direction="row" spacing={1} style={{ marginTop: 8 }}>
                          <LoadingButton
                            size="small"
                            variant="contained"
                            onClick={() => setSelectedForAllInGroup(group.key, true)}
                            disabled={isIndicesReadOnly || !(group.indicators || []).length}
                          >
                            Выбрать все
                          </LoadingButton>
                          <LoadingButton
                            size="small"
                            variant="contained"
                            onClick={() => setSelectedForAllInGroup(group.key, false)}
                            disabled={
                              isIndicesReadOnly ||
                              !(group.indicators || []).some((ind) =>
                                isGroupIndicatorInPlan(group, ind)
                              )
                            }
                          >
                            Снять выбор со всех
                          </LoadingButton>
                        </Stack>

                        <div
                          style={{
                            border: "1px solid #e0e0e0",
                            borderRadius: 4,
                            marginTop: 8,
                            padding: 8,
                          }}
                        >
                          <Table>
                            <TableBody>
                              {(group.indicators || []).map((item) => (
                                <TableRow key={`${group.key}_${item.id}`}>
                                  <TableCell variant="head" style={{ fontWeight: "bold" }}>
                                    {item.name}
                                  </TableCell>
                                  <TableCell align="right" width={56}>
                                    <Checkbox
                                      checked={isGroupIndicatorInPlan(group, item)}
                                    disabled={isIndicesReadOnly}
                                      onChange={(event) =>
                                        toggleGroupIndicator(
                                          group.key,
                                          item,
                                          event.target.checked
                                        )
                                      }
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>

                        <Stack direction="row" justifyContent="flex-end" style={{ marginTop: 8 }}>
                          {(indicatorGroups.length > 1 || groupIndex > 0) && (
                            <IconButton
                              size="small"
                              onClick={() => removeIndicatorGroup(group.key)}
                              disabled={isIndicesReadOnly}
                            >
                              <DeleteOutlineIcon />
                            </IconButton>
                          )}
                        </Stack>
                      </div>
                    ))}
                  </Grid>}

                {showIndicesSection && (
                <Grid item xs={12} style={{ paddingTop: 8 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Индикатор</TableCell>
                        <TableCell>План (иссл.)</TableCell>
                        {showResearchCodeColumn && (
                          <TableCell>Код исследования</TableCell>
                        )}
                        {showPlannedSumColumn && <TableCell>План (сумма)</TableCell>}
                        {!isIndicesReadOnly && <TableCell />}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {planIndexTableGroups.map((section) => {
                        const sampleCount =
                          section.entries[0]?.row?.plannedPlan != null &&
                          section.entries[0].row.plannedPlan !== ""
                            ? section.entries[0].row.plannedPlan
                            : 1;
                        const headerColSpan =
                          2 +
                          (showResearchCodeColumn ? 1 : 0) +
                          (showPlannedSumColumn ? 1 : 0) +
                          (!isIndicesReadOnly ? 1 : 0);
                        const sampleFieldDisabled =
                          isIndicesReadOnly && !canEditNceIndices;
                        return (
                        <React.Fragment key={section.groupKey}>
                          <TableRow>
                            <TableCell
                              colSpan={headerColSpan}
                              sx={{
                                bgcolor: "action.hover",
                                fontWeight: 600,
                                borderBottom: "2px solid",
                                borderColor: "divider",
                                py: 1.25,
                              }}
                            >
                              <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                spacing={2}
                                flexWrap="wrap"
                              >
                                <span>{section.departmentLabel}</span>
                                <TextField
                                  label="Количество проб"
                                  type="number"
                                  size="small"
                                  disabled={sampleFieldDisabled}
                                  value={sampleCount}
                                  onChange={(event) =>
                                    updateSectionSampleCount(
                                      section.groupKey,
                                      event.target.value
                                    )
                                  }
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">шт</InputAdornment>
                                    ),
                                  }}
                                  inputProps={{ min: 0, step: 1 }}
                                  sx={{ width: 200, bgcolor: "background.paper" }}
                                />
                              </Stack>
                            </TableCell>
                          </TableRow>
                          {section.entries.map(({ row, fullIdx }) => (
                            <TableRow key={row.id || `new_${fullIdx}`}>
                              <TableCell>
                                {row.indexName || `Индекс ${row.indexId}`}
                              </TableCell>
                              <TableCell>
                                <TextField
                                  fullWidth
                                  type="number"
                                  disabled={isIndicesReadOnly && !canEditNceIndices}
                                  value={row.plannedResearch}
                                  onChange={(event) =>
                                    updatePlanIndexField(
                                      fullIdx,
                                      "plannedResearch",
                                      event.target.value
                                    )
                                  }
                                />
                              </TableCell>
                              {showResearchCodeColumn && (
                                <TableCell>
                                  <ResearchCodeSelectField
                                    disabled={!canEditNceIndices || isAfterSentForApproval}
                                    groupId={row?.groupId}
                                    indicatorName={row?.indexName}
                                    valueServiceId={row?.serviceId ?? null}
                                    valueService={
                                      row?.service ||
                                      serviceByIdCache?.[Number(row?.serviceId)] ||
                                      null
                                    }
                                    loadServices={loadServicesForIndicator}
                                    onSelect={(service) => {
                                      if (!service?.id) return;
                                      const sid = Number(service.id);
                                      setServiceByIdCache((prev) => ({
                                        ...prev,
                                        [sid]: service,
                                      }));
                                      updatePlanIndexField(fullIdx, "serviceId", sid);
                                    }}
                                  />
                                </TableCell>
                              )}
                              {showPlannedSumColumn && (
                                <TableCell>
                                  <TextField
                                    fullWidth
                                    type="number"
                                    disabled={
                                      (isIndicesReadOnly && !canEditNceIndices) ||
                                      (isNceCodeSelectionStage && !row._manualSum)
                                    }
                                    value={row.plannedSum}
                                    onChange={(event) =>
                                      updatePlanIndexField(
                                        fullIdx,
                                        "plannedSum",
                                        event.target.value
                                      )
                                    }
                                    helperText={
                                      showManualSumHint && row._manualSum
                                        ? "Цена не найдена — введите вручную"
                                        : ""
                                    }
                                    sx={
                                      showManualSumHint && row._manualSum
                                        ? {
                                            "& .MuiOutlinedInput-root": {
                                              "& fieldset": {
                                                borderColor: "#ed6c02",
                                              },
                                              "&:hover fieldset": {
                                                borderColor: "#e65100",
                                              },
                                              "&.Mui-focused fieldset": {
                                                borderColor: "#ed6c02",
                                              },
                                            },
                                            "& .MuiFormHelperText-root": {
                                              color: "#ed6c02",
                                              fontSize: "0.7rem",
                                            },
                                          }
                                        : {}
                                    }
                                  />
                                </TableCell>
                              )}
                              {!isIndicesReadOnly && (
                                <TableCell>
                                  <IconButton
                                    size="small"
                                    onClick={() => removePlanIndex(fullIdx)}
                                  >
                                    <DeleteOutlineIcon />
                                  </IconButton>
                                </TableCell>
                              )}
                            </TableRow>
                          ))}
                        </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>

                  {loadingIndices && (
                    <Typography variant="body2" style={{ paddingTop: 8 }}>
                      Загрузка индексов...
                    </Typography>
                  )}
                </Grid>
                )}
              </Grid>
                    </Paper>
                </Grid>

                {!review && (
            <LoadingButton
              style={{ marginTop: "10px" }}
              loading={loadingSubmit}
              variant="contained"
              onClick={submit}
            >
                        Сохранить
                    </LoadingButton>
                )}

                {alert.render()}
        </>
      )}
        </DashboardLayout>
  );
};

export default PlanEditForm;